/**
 * Obtiene el mejor nombre de visualización posible para un dispositivo siguiendo un orden de prioridad.
 * También limpia el nombre resultante para una mejor presentación.
 *
 * @param {string} udid - El UDID del dispositivo. Es el identificador único y el último recurso si no se encuentra otro nombre.
 * @param {object | null} device - El objeto del dispositivo que viene de Firestore. Puede ser `null` si el documento no existe.
 * @param {object} deviceNameMap - El mapa que relaciona UDIDs con nombres "amigables" (generalmente de la galería de Jamf).
 * @returns {string} El mejor nombre de visualización encontrado y procesado, o "Sin Asignar" si no se encuentra ninguno válido.
 */
export const getBestDeviceName = (udid, device, deviceNameMap) => {
  // --- Cadena de Prioridad para encontrar la fuente del nombre ---
  // 1. Prioridad Máxima: El nombre del mapa de la galería (ej: "iPad 01").
  //    Usamos optional chaining (?.) por si `deviceNameMap` no estuviera definido.
  // 2. Segunda Prioridad: El nombre guardado en el documento del dispositivo en Firestore (ej: "iPad de Juan").
  //    Usamos optional chaining (?.) por si `device` es null o no tiene la propiedad `name`.
  // 3. Último Recurso: El UDID del dispositivo.
  const nameSource = deviceNameMap?.[udid] || device?.name || udid;

  // Si por alguna razón no tenemos ninguna fuente de nombre (lo cual es muy raro si hay un UDID),
  // devolvemos un valor por defecto para evitar errores.
  if (!nameSource) {
    return 'Sin Asignar';
  }

  // --- Limpieza y Formateo del Nombre ---
  // Convertimos la fuente a String para poder usar métodos de texto de forma segura.
  const processedName = String(nameSource)
    // Reemplaza la palabra "iPad" (sin importar mayúsculas/minúsculas) solo si es una palabra completa.
    // El `\b` (word boundary) evita que se reemplace en palabras como "iPadding".
    .replace(/\biPad\b/gi, '')
    // Elimina espacios en blanco al principio y al final.
    .trim();

  // Si después de la limpieza el nombre queda vacío (ej: el nombre original era solo "iPad "),
  // devolvemos "Sin Asignar". Si no, devolvemos el nombre ya procesado.
  return processedName || 'Sin Asignar';
};